/**
 * Admin API client for MiraVision.
 *
 * All privileged operations (Cognito admin, SES email, DynamoDB writes)
 * are routed through authenticated Lambda endpoints instead of calling
 * AWS SDK directly from the browser.
 *
 * Requires REACT_APP_ADMIN_API_BASE to be set in the environment.
 *
 * Usage:
 *   import { createUser, listTeamMembers, removeTeamMember, sendFeedback, deleteAnalysis } from '../utils/apiClient';
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE;

if (!API_BASE) {
  console.error('REACT_APP_ADMIN_API_BASE is not set – admin API calls will fail');
}

/**
 * Get the Authorization header with the current Cognito ID token.
 * The ID token contains the user's claims (custom:bucket, custom:isAdmin, etc.)
 * which the Cognito authorizer on API Gateway validates and passes to Lambda.
 */
async function getAuthHeaders() {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('No ID token available – user may not be signed in');
  }

  return {
    Authorization: idToken,
    'Content-Type': 'application/json',
  };
}

/**
 * Create a new user in the caller's organization.
 */
export async function createUser(email, firstName, lastName) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/admin/users`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, firstName, lastName }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to create user (${res.status})`);
  }

  return res.json();
}

/**
 * List all team members in the caller's Cognito group.
 */
export async function listTeamMembers() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/admin/team`, {
    method: 'GET',
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to list team (${res.status})`);
  }

  const data = await res.json();
  return data.users;
}

/**
 * Remove a user from the team (remove from group + disable).
 */
export async function removeTeamMember(username) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/admin/team/${encodeURIComponent(username)}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to remove user (${res.status})`);
  }

  return res.json();
}

/**
 * Send feedback email via the Lambda.
 */
export async function sendFeedback(feedbackText) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/feedback`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ feedback: feedbackText }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to send feedback (${res.status})`);
  }

  return res.json();
}

/**
 * Allocate a session on the shared backend.
 *
 * For the new ALB-based backend (REACT_APP_BACKEND_URL set):
 *   - Registers session in DynamoDB miravision_sessions
 *   - Ensures GPU ECS service has >= 1 running task
 *   - Returns { sessionId, backendUrl }
 *
 * Falls back to the legacy EC2 launch API if REACT_APP_BACKEND_URL is not set.
 */
export async function allocateSession(sessionId, deviceBucket, deviceName) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  if (backendUrl) {
    // New backend: GPU service auto-scales via ECS and handles sessions
    // via /create_session (called by useEC2Session). No separate allocation
    // Lambda needed — just store session metadata client-side.
    sessionStorage.setItem('sessionId', sessionId);
    sessionStorage.setItem('device_bucket', deviceBucket);
    sessionStorage.setItem('device_name', deviceName);
    return { sessionId, backendUrl };
  }

  // Legacy: launch EC2 instance
  const legacyUrl = 'https://0695pycn1k.execute-api.us-east-1.amazonaws.com/prod/instances';
  const res = await fetch(`${legacyUrl}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      body: { sessionId, device_bucket: deviceBucket, device_name: deviceName },
    }),
  });

  const data = await res.json();
  const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;

  if (!res.ok) {
    throw new Error(parsed?.error || 'Failed to start analysis session');
  }

  return { sessionId, backendUrl: null };
}

/**
 * Soft-delete an analysis in the Analysis_folders table.
 */
export async function deleteAnalysis(deviceBucket, analysisId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/analysis/${encodeURIComponent(analysisId)}`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ device_bucket: deviceBucket }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Failed to delete analysis (${res.status})`);
  }

  return res.json();
}
